// filepath: /Users/shiva/Dev/react-app/src/hooks/useIdToken.ts
import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { logger } from '../utils/logger';

// Create a module-specific logger
const log = logger.getLogger('useIdToken');

/**
 * Custom hook to access the Auth0 ID token
 * @returns An object containing the ID token and related utility functions
 */
export const useIdToken = () => {
  const [idToken, setIdToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  const { getIdTokenClaims, isAuthenticated } = useAuth0();
  
  // Function to fetch the ID token
  const fetchIdToken = useCallback(async () => {
    if (!isAuthenticated) {
      setIdToken(null);
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      const claims = await getIdTokenClaims();
      // The raw ID token is stored in the special property __raw
      const rawIdToken = claims?.__raw || null;
      
      // For debugging purposes, also save to localStorage
      if (rawIdToken) {
        localStorage.setItem('idToken', rawIdToken);
        log.log('ID token refreshed and stored in localStorage');
      } else {
        localStorage.removeItem('idToken');
        log.warn('No ID token received from Auth0');
      }
      
      setIdToken(rawIdToken);
    } catch (err) {
      log.error('Error fetching ID token:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      localStorage.removeItem('idToken');
    } finally {
      setIsLoading(false);
    }
  }, [getIdTokenClaims, isAuthenticated]);
  
  // Fetch the ID token when authentication state changes
  useEffect(() => {
    fetchIdToken();
  }, [fetchIdToken, isAuthenticated]);
  
  return {
    idToken,
    isLoading,
    error,
    refreshIdToken: fetchIdToken
  };
};